import * as React from 'react';

// Styles.
import { StyledComponent } from 'styled-components';
import {
  ButtonHTMLAttributes,
  HTMLAttributes,
  LabelHTMLAttributes
} from 'react';
import {
  Regular,
  Heading1,
  Heading2,
  Heading3,
  Heading4,
  Heading5,
  Heading6,
  PageTitle,
  Body1,
  Body2,
  Body3,
  Subtitle1,
  Subtitle2,
  Subtitle3,
  Button,
  ButtonSmall,
  Label,
  Caption,
  Overline,
  Hint,
  Feedback,
  BaseStyleProps,
  Span
} from './Typography.style';

// Allowed `kind` values.
export type TypeKind =
  | 'regular'
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'pageTitle'
  | 'body1'
  | 'body2'
  | 'body3'
  | 'button'
  | 'buttonSmall'
  | 'subtitle1'
  | 'subtitle2'
  | 'subtitle3'
  | 'caption'
  | 'label'
  | 'overline'
  | 'hint'
  | 'feedback'
  | 'span'
  | 'default';

type HtmlTextType =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'div'
  | 'span'
  | 'p'
  | 'label';

type TypographyElement =
  | HTMLHeadingElement
  | HTMLLabelElement
  | HTMLParagraphElement
  | HTMLSpanElement;

/**
 * Returns the component and its default style props for the kind of element
 * passed in via `props`.
 *
 * @param kind Kind of element.
 *
 * @return Object containing the component to render and its default props.
 */
const getComponent = (
  kind: TypeKind
): {
  component: StyledComponent<HtmlTextType, any>;
  defaultProps?: any;
} => {
  const kindMap = {
    regular: {
      component: Regular,
      defaultProps: {
        fontSize: '1rem',
        lineHeight: 1.5,
        letterSpacing: '0.00938em'
      }
    },
    h1: {
      component: Heading1,
      defaultProps: {
        fontSize: '3.815rem',
        lineHeight: 1.25
      }
    },
    h2: {
      component: Heading2,
      defaultProps: {
        fontSize: '3.052rem',
        lineHeight: 1.25
      }
    },
    h3: {
      component: Heading3,
      defaultProps: {
        fontSize: '2.441rem',
        lineHeight: 1.25
      }
    },
    h4: {
      component: Heading4,
      defaultProps: {
        fontSize: '1.953rem',
        lineHeight: 1.25
      }
    },
    h5: {
      component: Heading5,
      defaultProps: {
        fontSize: '1.563rem',
        lineHeight: 1.25
      }
    },
    h6: {
      component: Heading6,
      defaultProps: {
        fontSize: '1.125rem',
        lineHeight: 1.44
      }
    },
    pageTitle: {
      component: PageTitle,
      defaultProps: {
        fontSize: '1.953rem',
        lineHeight: 1.25
      }
    },
    body1: {
      component: Body1,
      defaultProps: {
        fontSize: '1rem',
        letterSpacing: '0.25px',
        lineHeight: 1.375
      }
    },
    body2: {
      component: Body2,
      defaultProps: {
        fontSize: '.875rem',
        letterSpacing: '0.25px',
        lineHeight: 1.285
      }
    },
    body3: {
      component: Body3,
      defaultProps: {
        fontSize: '.8125rem',
        letterSpacing: '0.25px',
        lineHeight: 1.1875
      }
    },
    subtitle1: {
      component: Subtitle1,
      defaultProps: {
        fontSize: '.875rem'
      }
    },
    subtitle2: {
      component: Subtitle2,
      defaultProps: {
        fontSize: '.875rem'
      }
    },
    subtitle3: {
      component: Subtitle3,
      defaultProps: {
        fontSize: '.75rem'
      }
    },
    button: {
      component: Button,
      defaultProps: {
        fontSize: '.9375rem'
      }
    },
    buttonSmall: {
      component: ButtonSmall,
      defaultProps: {
        fontSize: '.75rem'
      }
    },
    caption: {
      component: Caption,
      defaultProps: {
        fontSize: '.8125rem',
        letterSpacing: '0.2px'
      }
    },
    label: {
      component: Label,
      defaultProps: {
        fontSize: '.8125rem'
      }
    },
    overline: {
      component: Overline,
      defaultProps: {
        fontSize: '.75rem',
        letterSpacing: '2px'
      }
    },
    hint: {
      component: Hint,
      defaultProps: {
        fontSize: '.75rem',
        color: 'form.text.label.enabled'
      }
    },
    feedback: {
      component: Feedback,
      defaultProps: {
        fontSize: '.75rem',
        color: 'state.danger'
      }
    },
    default: {
      component: Regular,
      defaultProps: {
        lineHeight: 1.25
      }
    },
    span: {
      component: Span,
      defaultProps: {
        fontSize: '1rem',
        lineHeight: 1.5,
        letterSpacing: '0.00938em'
      }
    },
  };
  return kindMap[kind];
};

export interface ITypographyProps
  extends BaseStyleProps,
    Omit<
      HTMLAttributes<
        HTMLHeadingElement | HTMLSpanElement | HTMLParagraphElement
      > &
        LabelHTMLAttributes<HTMLLabelElement> &
        ButtonHTMLAttributes<HTMLButtonElement>,
      'color'
    > {
  /**
   * Kind of typography element to render.
   */
  kind?: TypeKind;
  /**
   * String that represents HTML element to use for the root node. Overrides the
   * root node behavior of the kind prop, for instance kind="subtitle1" as="h2"
   * will apply the styles from the subtitle1 but render the root node as <h2>
   * HTML element.
   */
  as?: HtmlTextType;
}

/**
 * Generic text rendering element.
 */
const Typography = React.forwardRef<TypographyElement, ITypographyProps>(
  ({ children, kind = 'regular', className, ...props }, ref) => {
    const { component, defaultProps } = getComponent(kind);
    return React.createElement(
      component,
      { ...defaultProps, ref, ...props, className: `uptime-typography ${className ?? ''}` },
      children
    );
  }
);

Typography.displayName = 'Typography';

export default Typography;
